// CSS
import './History.css';
// React hooks
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
// Skybase hooks
import { useData } from '../../context/DataContext';
import { useQuery } from '../../context/QueryContext';
import { useAuth } from '../../context/AuthContext';
// Skybase components
import Sidebar from '../../components/Sidebar/Sidebar';
import SearchHistoryIcon from '../../assets/EngineSelectorIcons/history-icon.png';
import SearchQueryHistorical from './elements/SearchQueryHistorical/SearchQueryHistorical';
import LoadingScreenOverlay from '../../components/LoadingScreenOverlay/LoadingScreenOverlay';
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover';
// External hooks/components
import { useAuth as useClerkAuth } from '@clerk/react';
import { useMediaQuery } from 'react-responsive';



// =======
// History – represents the /history Product page.
// =======
function History(){
    
    // destructure global context
    const { isLoading, disableLoadingScreen, activeEngineJson } = useData();
    const { activeEngineSearchEvents, changeSelectedSearchResultJson } = useQuery();
    const { checkIsSubscribed, activeUserJson } = useAuth();
    const { isLoading: isClerkLoading, getToken } = useClerkAuth();

    // init hooks
    const navigate = useNavigate('');

    // responsive CSS
    const isDesktop = useMediaQuery({ minWidth: 1025 });

    // ================
    // onMount Behavior
    // ================
    // onMount --> check for valid Skybase Stripe subscription
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    useEffect(() => {
        
        // Async. fn that prevents CurrentUsers without an active subscription from interacting with this page.
        const checkSubscription = async () => {
            // Don't perform a subscription-check if the Clerk User hasn't loaded yet.
            if (isClerkLoading) return;

            // Does CurrentUser have an active subscription?
            const subscribed = await checkIsSubscribed();

            // If NO: Display InformationalPopover.
            if (!subscribed) { setIsInformationalPopoverOpen(true); }
        };

        // Run the async. subscription-check
        checkSubscription();

        // Disable any loading screens.
        disableLoadingScreen();

        // Edge case: "bfcache === true" --> Forcibly reload the page to re-run the async. subscription-check
        const handlePageShow = (event) => {
            if (event.persisted) { window.location.reload(); }
        };
        window.addEventListener("pageshow", handlePageShow); // "pageShow" event listener

        // Edge case: No <Clerk JWT> --> Immediately redirect user from this page.
        checkForValidClerkTokenOtherwiseRedirect();

        // Remove listeners onDismount (prevent memory overflow)
        return () => { window.removeEventListener("pageshow", handlePageShow); };
    }, [isClerkLoading])

    
    // ==========================================
    // checkForValidClerkTokenOtherwiseRedirect()
    // ==========================================
    // Checks for a valid clerk token. Forcibly re-directs the user to '/login' if not.
    async function checkForValidClerkTokenOtherwiseRedirect() {
        let token = await getToken();
        if (!token) { navigate('../login') };
    }
    // onChange ('activeUserJson') --> immediately check again for valid subscription
    useEffect(() => {
        checkForValidClerkTokenOtherwiseRedirect();
    }, [activeUserJson]);

    
    // ===================
    // SEARCH PAGE FADE-IN
    // ===================
    // re-animate upon hydration
    useEffect(() => {
        
        // Select all elements with this class
        const elements = document.querySelectorAll('.SearchPage-fade-in');

        elements.forEach(el => {
            // Remove animation class
            el.classList.remove('SearchPage-fade-in');

            // Force reflow
            void el.offsetWidth;

            // Re-add animation class
            el.classList.add('SearchPage-fade-in');
        });

    }, [activeEngineJson]); // Re-run whenever items change


    // ==============================
    // HANDLE onClick OF SEARCH EVENT
    // ==============================
    // vars
    const estimatedLoadingTimeMilliseconds = 700;                           
    // onClick
    const handleQueryClick = (json) => {
        // update JSON state var
        changeSelectedSearchResultJson(json);

        // immediately re-direct to /cache Product page
        navigate('../cache');
    }

    
    // JSX Component
    return(
        <div className='Layout'>
            
            {/* Sidebar */}
            <Sidebar/>

            {/* Main page content */}
            <div className='History'>
                
                {/* Condition: NO isLoading */}
                {!isLoading && 
                    <>
                    
                    {/* History repeater header */}
                    <div className='History-repeater-header SearchPage-fade-in'>
                        
                        {/* Label: "Search Query" */}
                        <div className='History-repeater-header-label--query'>Search Query</div>
                        
                        
                        {/* Right content */}
                        { isDesktop && 
                            <div className='History-repeater-header-right-content'>

                                {/* Label: "Date" */}
                                <div className='History-repeater-header-label--date'>Date</div>

                                {/* Label: "Local Time Zone" */}
                                <div className='History-repeater-header-label--time-zone'>Time ({Intl.DateTimeFormat('en-US', {timeZoneName: 'short'}).format(new Date()).split(' ').pop()})</div>

                                {/* Num. Results */}
                                <div className='History-repeater-header-label--num-results'># Results</div>

                            </div>
                        }

                    </div>

                    {/* History repeater */}
                    <div className='History-repeater'>
                        
                        {/* Condition: Search Events exist */}
                        { (activeEngineSearchEvents?.length > 0) && 
                            <>
                                {/* REAL EVENTS */}
                                {activeEngineSearchEvents?.map( (search_event_json, index) => (
                                    <div
                                        key={search_event_json.id}
                                        className='History-repeater-instance'
                                        style={{ "--stagger-index": Math.min(index, 20) }}
                                        onClick={() => handleQueryClick(search_event_json)}
                                    >                    
                                        <SearchQueryHistorical id={search_event_json.id} />  
                                </div>  
                                ))}
                            </>
                        }

                        {/* Edge condition: NO Search Events */}
                        { (activeEngineSearchEvents?.length === 0 &&
                            <div className='History-repeater--empty-text'>
                                
                                {/* Main */}
                                {activeEngineJson && <>
                                    <span className='SearchPage-fade-in'>You have not made any searches in this database.</span>
                                    <div className='History-repeater--empty-text-cta SearchPage-fade-in' onClick={() => navigate('../search')}>Start searching this database</div>
                                </>}

                                {/* Edge case */}
                                {!activeEngineJson && <>
                                    <span className='SearchPage-fade-in'>Select a database to view your search history.</span>
                                    <div className='History-repeater--empty-text-cta SearchPage-fade-in' onClick={() => navigate('../db')}>Select a database to search</div>
                                </>}

                            </div>
                        )}
                    </div>
                    </>
                }

                {/* Condition: YES isLoading */}
                {isLoading && 
                    <LoadingScreenOverlay
                        messagesToDisplay={["Locating previous results", "Retrieving previous results", "Downloading previous results", "Restoring previous results", "Displaying previous results"]}
                        estimatedTimeMilliseconds={estimatedLoadingTimeMilliseconds}
                        optionalActionMessage={"Displaying previous results"}
                    />
                }

                {/* InformationalPopover */}
                { isInformationalPopoverOpen &&
                    <InformationalPopover
                        titleText={"Subscription Required"}
                        bodyText={"Please purchase a Skybase subscription plan to continue."}
                        primaryButtonText={"View Subscription Plans"}
                        primaryButtonCallback={() => navigate('../pricing')}
                        secondaryButtonText={"Return Home"}
                        secondaryButtonCallback={() => navigate('../')}
                    />
                }
            </div>
        </div>
    )
}



// Export statement
export default History;